import { useRouter } from "next/router";
import DOMPurify from "isomorphic-dompurify";
import Layout from "@/components/ui/Layout";

function sanitizeHtml(html) {
  if (!html) return "";

  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ["script", "style", "meta", "link", "iframe", "object", "embed"],
    FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "style"],
  });
}

function RenderHtml({ html }) {
  const safe = sanitizeHtml(html);
  if (!safe) return null;

  return (
    <div
      className="prose dark:prose-invert max-w-none"
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}

function stripHtmlForMeta(input) {
  const s = (input ?? "").toString();

  const noTags = s.replace(/<[^>]*>/g, " ");

  const decoded = noTags
    .replace(/&nbsp;|&#160;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">");

  return decoded.replace(/\s+/g, " ").trim();
}

function ellipsis(str, max) {
  const s = (str ?? "").toString().trim();
  if (!s) return "";
  if (s.length <= max) return s;
  return s.slice(0, Math.max(0, max - 3)).trimEnd() + "...";
}

export default function PageSlug({ page }) {
  const router = useRouter();

  // blocking fallbacknál ez bőven elég
  if (router.isFallback) {
    return (
      <Layout
        title="Betöltés..."
        description=" "
        ogPath=""
        ogType="website"
        center={
          <div className="mx-auto w-full max-w-3xl px-4 py-8">
            <div className="text-sm text-gray-600 dark:text-gray-300">Betöltés…</div>
          </div>
        }
      />
    );
  }

  const title = (page?.title || "Oldal").trim();
  const metaDescription = ellipsis(stripHtmlForMeta(page?.content), 160);

  return (
    <Layout
      title={title}
      description={metaDescription || " "}
      ogPath={router.asPath || `/${page?.slug || ""}`}
      ogType="website"
      center={
        <div className="mx-auto w-full max-w-3xl px-4 py-8">
          {page?.content ? (
            <RenderHtml html={page.content} />
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Ehhez az oldalhoz még nincs tartalom.
            </p>
          )}
        </div>
      }
    />
  );
}

export async function getStaticPaths() {
  try {
    const res = await fetch(`${process.env.STRAPI_URL}/api/pages`);
    if (!res.ok) {
      return {
        paths: [],
        fallback: "blocking",
      };
    }

    const json = await res.json();

    const paths = (json?.data || [])
      .map((item) => item?.slug)
      .filter(Boolean)
      .map((slug) => ({
        params: { slug },
      }));

    return {
      paths,
      fallback: "blocking",
    };
  } catch (error) {
    return {
      paths: [],
      fallback: "blocking",
    };
  }
}

export async function getStaticProps({ params }) {
  const slug = params?.slug;

  if (!slug) return { notFound: true };

  try {
    const url =
      `${process.env.STRAPI_URL}/api/pages` +
      `?filters[slug][$eq]=${encodeURIComponent(slug)}`;

    const res = await fetch(url);

    if (!res.ok) return { notFound: true };

    const json = await res.json();
    const page = json?.data?.[0] || null;

    if (!page) return { notFound: true };

    return {
      props: { page },
      revalidate: 3600,
    };
  } catch (error) {
    return { notFound: true };
  }
}